import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileSrc } from "@/lib/api";
import { FileCard } from "@/components/FileCard";
import { Loader2, Clock, Users2, Building2, Quote, ChevronRight, Sparkles } from "lucide-react";

function fmt(d) {
  if (!d) return "Unknown date";
  try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return d; }
}

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  useEffect(() => { setP(null); api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => {}); }, [id]);

  if (!p) return <div className="flex justify-center h-96 items-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-10">
      <nav className="flex items-center gap-1.5 text-sm text-slate-500">
        <Link to="/projects" className="hover:text-white">Projects</Link>
        <ChevronRight className="w-3.5 h-3.5" /><span className="text-slate-300">{p.name}</span>
      </nav>

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl border border-white/5 h-64 md:h-72">
        <img src={p.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/60 to-transparent" />
        <div className="absolute bottom-0 p-8">
          <div className="text-[11px] uppercase tracking-[0.2em] text-blue-300 mb-2">{p.type}</div>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">{p.name}</h1>
          <div className="text-sm text-slate-400 mt-2">{p.file_count} supporting file(s)</div>
        </div>
      </div>

      {/* Executive summary */}
      <div className="rounded-xl border border-white/5 bg-[#111827] p-6 md:p-8">
        <div className="flex items-center gap-2 text-blue-400 text-[11px] uppercase tracking-wider mb-3"><Sparkles className="w-3.5 h-3.5" /> AI Executive Summary</div>
        <p className="text-slate-200 text-lg leading-relaxed">{p.executive_summary || "No summary available yet."}</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-10">
          {/* Timeline */}
          {p.timeline?.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold text-white mb-5 flex items-center gap-2"><Clock className="w-5 h-5 text-blue-400" /> Development Timeline</h2>
              <div className="relative pl-6 border-l border-white/10 space-y-6">
                {p.timeline.map((t) => (
                  <div key={t.file_id} className="relative">
                    <div className="absolute -left-[27px] top-1 w-3 h-3 rounded-full bg-blue-500 ring-4 ring-[#0B0F19]" />
                    <Link to={`/file/${t.file_id}`} className="block group">
                      <div className="text-xs text-slate-500 font-mono">
                        {fmt(t.date)} {t.is_upload_date && <span className="text-amber-500/80">(upload date)</span>}
                      </div>
                      <div className="text-white group-hover:text-blue-300 transition-colors font-medium">{t.title}</div>
                      <div className="text-[11px] uppercase tracking-wide text-slate-600">{t.kind}</div>
                    </Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Gallery */}
          {p.gallery?.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold text-white mb-4">Media Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {p.gallery.map((gid) => (
                  <Link key={gid} to={`/file/${gid}`} className="rounded-lg overflow-hidden border border-white/10 aspect-video bg-[#070B14]">
                    <img src={fileSrc(gid)} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Citations */}
          {p.citations?.length > 0 && (
            <section>
              <h2 className="font-display text-xl font-bold text-white mb-4">Key Excerpts & Citations</h2>
              <div className="space-y-3">
                {p.citations.map((c, i) => (
                  <div key={i} className="rounded-xl border border-white/5 bg-[#111827] p-5">
                    <Quote className="w-4 h-4 text-blue-400 mb-2" />
                    <p className="text-slate-300 text-sm italic leading-relaxed">"{c.excerpt}"</p>
                    <Link to={`/file/${c.file_id}`} className="text-xs text-blue-400 hover:text-blue-300 mt-2 inline-block">— Source: {c.source}</Link>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Supporting files */}
          <section>
            <h2 className="font-display text-xl font-bold text-white mb-4">Supporting Materials</h2>
            {p.files?.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-5">{p.files.map((f, i) => <FileCard key={f.id} f={f} index={i} />)}</div>
            ) : <p className="text-slate-600 text-sm">No files yet.</p>}
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {p.people?.length > 0 && (
            <div className="rounded-xl border border-white/5 bg-[#111827] p-5">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3"><Users2 className="w-4 h-4 text-blue-400" /> People</h3>
              <div className="flex flex-wrap gap-2">{p.people.map((n) => <span key={n} className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300">{n}</span>)}</div>
            </div>
          )}
          {p.organizations?.length > 0 && (
            <div className="rounded-xl border border-white/5 bg-[#111827] p-5">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-blue-400" /> Organizations</h3>
              <div className="flex flex-wrap gap-2">{p.organizations.map((n) => <span key={n} className="text-xs px-2.5 py-1 rounded-full bg-white/5 text-slate-300">{n}</span>)}</div>
            </div>
          )}
          {p.tags?.length > 0 && (
            <div className="rounded-xl border border-white/5 bg-[#111827] p-5">
              <h3 className="text-sm font-semibold text-white mb-3">Tags & Classifications</h3>
              <div className="flex flex-wrap gap-2">{p.tags.map((t) => <Link key={t} to={`/search?q=${encodeURIComponent(t)}`} className="text-xs px-2.5 py-1 rounded-full bg-blue-600/10 text-blue-300 border border-blue-500/20 hover:bg-blue-600/20">{t}</Link>)}</div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
